/**
 * BillingCreditSection Component
 *
 * Displays credit balance breakdown and allows purchasing additional credit packs.
 * Shows monthly credits, addon credits, rollover credits, and total available.
 */

import { useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Coins, Loader2, ShoppingCart, Sparkles } from 'lucide-react';

interface BillingCreditSectionProps {
  monthlyCredits: number;
  addonCredits: number;
  rolloverCredits: number;
  isPro: boolean;
}

const API_URL = import.meta.env.VITE_API_URL || '';

export function BillingCreditSection({
  monthlyCredits,
  addonCredits,
  rolloverCredits,
  isPro,
}: BillingCreditSectionProps) {
  const { getToken } = useAuth();
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const totalCredits = monthlyCredits + addonCredits + rolloverCredits;

  const handleBuyCreditPack = async () => {
    setIsPurchasing(true);
    setError(null);

    try {
      const token = await getToken();
      const response = await fetch(`${API_URL}/api/stripe/buy-credits`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to start purchase');
      }

      // Redirect to Stripe Checkout
      if (data.data?.url) {
        window.location.href = data.data.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      console.error('Credit purchase error:', err);
      setError(err instanceof Error ? err.message : 'Failed to start purchase');
      setIsPurchasing(false);
    }
  };

  if (!isPro) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Coins className="h-5 w-5" />
            AI Generation Credits
          </CardTitle>
          <CardDescription>
            Upgrade to Pro to access credit-based billing and rollover benefits
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="rounded-lg border-2 border-dashed border-muted py-8 text-center">
            <Sparkles className="mx-auto h-8 w-8 text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">
              Pro plan includes 10 monthly credits with rollover and add-on packs
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Coins className="h-5 w-5" />
          AI Generation Credits
        </CardTitle>
        <CardDescription>
          Track your credit balance and purchase additional credits as needed
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Total Credits Display */}
        <div className="flex items-center justify-between rounded-lg bg-primary/5 p-4">
          <div>
            <p className="text-sm font-medium text-muted-foreground">Total Available</p>
            <p className="text-3xl font-bold text-primary">{totalCredits}</p>
            <p className="text-xs text-muted-foreground">credits</p>
          </div>
          <Badge variant="secondary" className="bg-primary/10 text-primary">
            <Sparkles className="mr-1 h-3 w-3" />
            Active
          </Badge>
        </div>

        {/* Credit Breakdown */}
        <div className="space-y-3">
          <h4 className="text-sm font-medium">Credit Breakdown</h4>

          <div className="space-y-2">
            <div className="flex items-center justify-between rounded-lg border bg-card p-3">
              <div>
                <p className="text-sm font-medium">Monthly Credits</p>
                <p className="text-xs text-muted-foreground">Included with Pro plan</p>
              </div>
              <span className="text-lg font-semibold">{monthlyCredits}</span>
            </div>

            <div className="flex items-center justify-between rounded-lg border bg-card p-3">
              <div>
                <p className="text-sm font-medium">Add-on Credits</p>
                <p className="text-xs text-muted-foreground">Purchased credit packs</p>
              </div>
              <span className="text-lg font-semibold">{addonCredits}</span>
            </div>

            <div className="flex items-center justify-between rounded-lg border bg-card p-3">
              <div>
                <p className="text-sm font-medium">Rollover Credits</p>
                <p className="text-xs text-muted-foreground">Unused from previous months</p>
              </div>
              <span className="text-lg font-semibold">{rolloverCredits}</span>
            </div>
          </div>
        </div>

        <Separator />

        {/* Purchase Credit Pack */}
        <div className="space-y-3">
          <h4 className="text-sm font-medium">Need More Credits?</h4>
          <div className="rounded-lg border bg-muted/30 p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <p className="font-medium">Credit Pack</p>
                <p className="text-sm text-muted-foreground mt-1">
                  10 additional AI generation credits
                </p>
                <p className="text-2xl font-bold mt-2">$100</p>
              </div>
              <Button
                onClick={handleBuyCreditPack}
                disabled={isPurchasing}
                size="lg"
              >
                {isPurchasing ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <ShoppingCart className="mr-2 h-4 w-4" />
                    Buy Pack
                  </>
                )}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              Credits never expire and automatically roll over month to month
            </p>
          </div>

          {error && (
            <p className="text-sm text-destructive">{error}</p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default BillingCreditSection;
